import { useEffect, useRef, useState } from "react";
import { useMapsLibrary } from "@vis.gl/react-google-maps";
import { Loader2, MapPin, Search } from "lucide-react";
import { useLang } from "@/i18n";

/**
 * Ricerca libera di luoghi e città con Google Places (AutocompleteSuggestion,
 * Places API New). onPick riceve { id: "gplace_<placeId>", name, lat, lng }.
 * Richiede di essere dentro GmapsProvider e "Places API (New)" sulla chiave.
 */
export const PlaceSearch = ({ onPick, regionCodes = ["it"], bias = null, testId = "place-google-search" }) => {
    const placesLib = useMapsLibrary("places");
    const { t } = useLang();
    const [q, setQ] = useState("");
    const [items, setItems] = useState([]);
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const sessionRef = useRef(null);
    const boxRef = useRef(null);
    const debRef = useRef(null);

    useEffect(() => {
        const close = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
        document.addEventListener("mousedown", close);
        return () => document.removeEventListener("mousedown", close);
    }, []);

    useEffect(() => {
        if (!placesLib?.AutocompleteSuggestion || q.trim().length < 3) { setItems([]); return undefined; }
        if (!sessionRef.current) sessionRef.current = new placesLib.AutocompleteSessionToken();
        clearTimeout(debRef.current);
        debRef.current = setTimeout(async () => {
            try {
                const req = { input: q.trim(), sessionToken: sessionRef.current, includedRegionCodes: regionCodes };
                if (bias) req.locationBias = { center: bias, radius: 40000 };
                const { suggestions } = await placesLib.AutocompleteSuggestion.fetchAutocompleteSuggestions(req);
                setItems((suggestions || []).filter((s) => s.placePrediction).slice(0, 6));
                setOpen(true);
            } catch {
                setItems([]);
            }
        }, 300);
        return () => clearTimeout(debRef.current);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [q, placesLib]);

    const choose = async (sugg) => {
        const pred = sugg.placePrediction;
        setLoading(true);
        try {
            const place = pred.toPlace();
            await place.fetchFields({ fields: ["location", "displayName", "formattedAddress"] });
            sessionRef.current = null; // chiude la sessione di fatturazione Places
            const name = typeof place.displayName === "string" ? place.displayName : (place.displayName?.text || pred.text?.text || "");
            onPick({ id: `gplace_${pred.placeId}`, name, lat: place.location.lat(), lng: place.location.lng() });
            setQ("");
            setItems([]);
            setOpen(false);
        } catch {
            // luogo non risolvibile: nessuna tappa aggiunta
        }
        setLoading(false);
    };

    return (
        <div ref={boxRef} className="relative">
            <div className="flex items-center gap-2 rounded-lg border border-amber-400/40 bg-[#0b0b0c] px-3 py-2.5 shadow-[0_0_18px_rgba(214,180,92,0.08)]">
                <Search size={15} className="shrink-0 text-amber-400" />
                <input
                    data-testid={testId}
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    onFocus={() => items.length > 0 && setOpen(true)}
                    placeholder={t.builder.placesPh}
                    className="w-full bg-transparent text-sm text-stone-200 placeholder:text-stone-500 focus:outline-none"
                />
                {loading && <Loader2 size={14} className="animate-spin text-amber-400" />}
            </div>
            {open && items.length > 0 && (
                <div data-testid={`${testId}-suggestions`} className="absolute z-30 mt-2 w-full overflow-hidden rounded-xl border border-amber-500/25 bg-[#101011] shadow-2xl">
                    {items.map((s) => (
                        <button
                            key={s.placePrediction.placeId}
                            data-testid={`${testId}-opt`}
                            onClick={() => choose(s)}
                            className="flex w-full items-center gap-2.5 px-3.5 py-2.5 text-left text-sm text-stone-200 transition-colors hover:bg-amber-400/10"
                        >
                            <MapPin size={13} className="shrink-0 text-amber-500/80" />
                            <span className="truncate">{s.placePrediction.text?.text}</span>
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
};
